import type { QueryClient } from '@tanstack/react-query';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BackendTypes } from '@tf/api';
import { gql, GraphQLClient } from '@tf/api';
import type { SearchHit } from '@tf/shared';
import { objectUriByNodeIdQueryOptions } from '@tf/shared';
import { createPublicSchemaId, objectRelationValue } from '@tf/utils';

import { useConfigContext } from '../hooks';

import { getJobRefetchInterval } from './jobUtils';

export type EntityAdhocSearchParams = {
	entityName: string;
};

export type PersonAdhocSearchParams = {
	firstName: string;
	middleName?: string;
	lastName: string;
	birthDate?: string;
};

export type AdhocSearchParams = {
	entity?: EntityAdhocSearchParams;
	person?: PersonAdhocSearchParams;
};

export type UnitAdhocSearch = {
	id: string;
	meta: {
		'@createdAt': string;
		'@updatedAt': string;
		'@status': 'PENDING' | 'IN_PROGRESS' | 'FAILED' | 'SUCCESS' | 'CANCELLED';
	};
	data: {
		payload: AdhocSearchParams;
		selectedMatchIds?: string[];
		comments?: {
			user?: string;
			date: string;
			comment: string;
		}[];
		result?: {
			rawResponse: { hits: SearchHit[] };
			searchId: number;
		};
	};
};

const latestBackgroundCheckQueryOptions = (
	queryClient: QueryClient,
	unitNodeId: BackendTypes.GraphId
) => {
	return queryOptions({
		queryKey: ['backgroundCheck', unitNodeId],
		queryFn: async (): Promise<UnitAdhocSearch | null> => {
			const unitObjectId = await queryClient.fetchQuery(objectUriByNodeIdQueryOptions(unitNodeId));
			const res = await GraphQLClient.getInstance().request(
				gql`
					query LatestUnitAdhocSearch($unitObjectId: JSON!) {
						get(
							rootFilter: {
								kind: "unit_adhoc_search"
								meta: { AND: [{ key: "/@with/unit", operator: EQ, value: $unitObjectId }] }
							}
							paginationFilter: { sortByMeta: "/@createdAt", sortByDirection: DESC, limit: 1 }
						) {
							id
							meta
							data
						}
					}
				`,
				{ unitObjectId }
			);
			return res.get[0] ?? null;
		},
		refetchInterval: (query) => {
			return getJobRefetchInterval(query.state.data);
		},
	});
};

export const useLatestBackgroundCheck = (unitNodeId: BackendTypes.GraphId) => {
	const queryClient = useQueryClient();
	return useQuery(latestBackgroundCheckQueryOptions(queryClient, unitNodeId));
};

export const useCreateBackgroundCheckMutation = () => {
	const queryClient = useQueryClient();
	const {
		apiConfig: { clusterId },
	} = useConfigContext((s) => s);

	return useMutation({
		mutationFn: async ({
			unitNodeId,
			adhocSearchParams,
		}: {
			unitNodeId: number;
			adhocSearchParams: AdhocSearchParams;
		}): Promise<{ id: string }> => {
			const unitObjectId = await queryClient.fetchQuery(objectUriByNodeIdQueryOptions(unitNodeId));
			const res = await GraphQLClient.getInstance().request(
				gql`
					mutation CreateUnitAdhocSearch($job: ObjectInput!) {
						mutate(create: [$job]) {
							id
							kind
						}
					}
				`,
				{
					job: {
						id: 'new://job_1',
						schema: createPublicSchemaId({
							clusterId,
							schemaTag: 'types/finance/job/unit_adhoc_search',
						}),
						data: {
							payload: {
								...adhocSearchParams,
								unit: objectRelationValue('unit', unitObjectId),
							},
						},
					},
				}
			);
			return res.mutate.find((obj: { kind: string }) => obj.kind === 'unit_adhoc_search');
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries(
				latestBackgroundCheckQueryOptions(queryClient, variables.unitNodeId)
			);
		},
	});
};

export const useUpdateBackgroundCheckMatchesMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			unitAdhocSearchId,
			matches,
		}: {
			unitNodeId: number; // needed only for cache invalidation
			unitAdhocSearchId: string;
			matches: {
				ids: string[];
				comments: BackendTypes.AdhocComment[] | null;
			};
		}) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation UpdateUnitAdhocSearchMatches($unitAdhocSearchId: ID!, $dataPatches: [JSONPatch!]!) {
						mutate(patch: [{ objectId: $unitAdhocSearchId, objectDataPatch: $dataPatches }]) {
							id
							kind
							meta(path: ["/@with/unit"])
						}
					}
				`,
				{
					unitAdhocSearchId,
					dataPatches: matches.comments?.length
						? [{ op: 'add', path: '/comments', value: matches.comments }]
						: [{ op: 'add', path: '/selectedMatchIds', value: matches.ids }],
				}
			);
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries(
				latestBackgroundCheckQueryOptions(queryClient, variables.unitNodeId)
			);
		},
	});
};
